import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomizedSwitch, NoItemsYet, StatusCard, ToolInfo } from 'components';
import { Images, renderTargetStatuses, targetStatusEnums, PermissionList, RolePermission } from 'utils';
import { goalActions, libraryActions } from 'store';
import { GoalStatusRow } from '../constants';
import { TargetMaintenance } from './targetMaintenance';
import { BaselineCriteriaOption } from './baselineCriteriaOption';
import { FirstTrialSwitch, toggleFirstTrial } from './firstTrialCriteria';
import { CriteriaRuleRow, CriteriaSaveBar, GOAL_MASTERY_FIELDS, isRuleValid, MASTERY_FIELDS, useCriteriaDraft } from './criteriaForm';

// Save action per card — the client goal and the library goal use different endpoints.
const SAVE_ACTIONS = {
    targetMastery: {
        client: { type: 'CHANGE_TARGET_MASTERY', action: goalActions.changeTargetMastery },
        library: { type: 'CHANGE_LIBRARY_GOAL_TARGET_MASTERY', action: libraryActions.changeLibraryGoalTargetMastery },
    },
    goalMastery: {
        client: { type: 'CHANGE_GOAL_MASTERY', action: goalActions.changeGoalMastery },
        library: { type: 'CHANGE_LIBRARY_GOAL_MASTERY', action: libraryActions.changeLibraryGoalMastery },
    },
};

/** Teaching -> Mastered rule (goal default for all targets). Shared by the client goal and the library goal. */
export const TargetMasteryCard = ({ goalById, isLibrary }) => {
    const dispatch = useDispatch();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const saveAction = SAVE_ACTIONS.targetMastery[isLibrary ? 'library' : 'client'];
    const targetMastery = useCriteriaDraft({ fields: MASTERY_FIELDS, actionType: saveAction.type });

    useEffect(() => {
        if (goalById?.targetMastery?._id) targetMastery.sync(goalById?.targetMastery);
    }, [goalById]);

    return (
        <div className="mastery-box">
            <p className="mastery-title">Target Mastery</p>
            <div className="mastery-info-box">
                <div className="mastery-status-row">
                    <div className="space-between-align-center" style={{ padding: 12 }}>
                        <div className="flex-align-center" style={{ gap: 4 }}>
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => targetStatusEnums.TEACHING === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => targetStatusEnums.TEACHING === j?.value)}
                            />
                            <img src={Images.arrowRight} alt="arrow" />
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => targetStatusEnums.MASTERED === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => targetStatusEnums.MASTERED === j?.value)}
                            />
                        </div>
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <FirstTrialSwitch
                                goal={goalById}
                                disabled={!hasPermission}
                                checked={targetMastery.draft?.firstTrialOnly}
                                onToggle={() => targetMastery.update(toggleFirstTrial(targetMastery.draft))}
                            />
                            <div className="switcher-style" style={{ gap: 16 }}>
                                <p style={{ width: 'auto', padding: 0 }}>Auto Mastery</p>
                                <CustomizedSwitch
                                    disabled={!hasPermission}
                                    checked={targetMastery.draft?.automastery}
                                    handleClick={() => targetMastery.update({ automastery: !targetMastery.draft?.automastery })}
                                />
                            </div>
                            <ToolInfo
                                text={
                                    'Automatically mark targets as mastered based on the selected sessions or days — ' +
                                    'consecutive, or on average — and % of targets mastered.'
                                }
                            />
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid #EBF2FD', padding: 12 }}>
                        <CriteriaRuleRow
                            goal={goalById}
                            subject="Master targets"
                            criteria={targetMastery.draft}
                            disabled={!hasPermission}
                            onChange={targetMastery.update}
                        />
                    </div>
                    <CriteriaSaveBar
                        dirty={targetMastery.isDirty}
                        saving={targetMastery.isSaving}
                        disabled={!isRuleValid(targetMastery.draft)}
                        onCancel={targetMastery.reset}
                        onSave={() => targetMastery.save((body) => dispatch(saveAction.action(goalById?.id, body)))}
                    />
                </div>
            </div>
        </div>
    );
};

/** Active -> Mastered rule for the goal itself. Shared by the client goal and the library goal. */
export const GoalMasteryCard = ({ goalById, isLibrary }) => {
    const dispatch = useDispatch();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const saveAction = SAVE_ACTIONS.goalMastery[isLibrary ? 'library' : 'client'];
    const goalMastery = useCriteriaDraft({ fields: GOAL_MASTERY_FIELDS, actionType: saveAction.type });

    useEffect(() => {
        if (goalById?.goalMastery?._id) goalMastery.sync(goalById?.goalMastery);
    }, [goalById]);

    return (
        <div className="mastery-box">
            <p className="mastery-title">Goal Mastery</p>
            <div className="mastery-info-box">
                <div className="mastery-status-row">
                    <div className="space-between-align-center" style={{ padding: 12 }}>
                        <div className="flex-align-center" style={{ gap: 4 }}>
                            <GoalStatusRow status={'ACTIVE'} />
                            <img src={Images.arrowRight} alt="arrow" />
                            <GoalStatusRow status={'MASTERED'} />
                        </div>
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <div className="switcher-style" style={{ gap: 16 }}>
                                <p style={{ width: 'auto', padding: 0 }}>Auto Mastery</p>
                                <CustomizedSwitch
                                    disabled={!hasPermission}
                                    checked={goalMastery.draft?.automastery}
                                    handleClick={() => goalMastery.update({ automastery: !goalMastery.draft?.automastery })}
                                />
                            </div>
                            <ToolInfo text={'Automatically mark a goal as mastered based on the % of its targets that are mastered.'} />
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid #EBF2FD', padding: 12 }}>
                        <div className="flex-align-center" style={{ gap: 10 }}>
                            <p className="target-mastery-info">Master Goal if </p>
                            <div className="mastery-percent-input-box">
                                <input
                                    disabled={!hasPermission}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        let value = raw === '' ? '' : +raw;
                                        if (typeof value === 'number' && value < 0) value = 0;
                                        if (typeof value === 'number' && value > 100) value = 100;
                                        goalMastery.update({ count: value });
                                    }}
                                    type="number"
                                    name="count"
                                    value={goalMastery.draft?.count ?? ''}
                                />
                                <p>%</p>
                            </div>
                            <p className="target-mastery-info">of targets are mastered.</p>
                        </div>
                    </div>
                    <CriteriaSaveBar
                        dirty={goalMastery.isDirty}
                        saving={goalMastery.isSaving}
                        disabled={typeof goalMastery.draft?.count !== 'number'}
                        onCancel={goalMastery.reset}
                        onSave={() => goalMastery.save((body) => dispatch(saveAction.action(goalById?.id, body)))}
                    />
                </div>
            </div>
        </div>
    );
};

export const MasteryOption = ({}) => {
    const goalById = useSelector((state) => state.goals.goalById);

    if (!goalById?.goalMastery?._id && !goalById?.targetMastery?._id && !goalById?.baselineCriteria?._id) {
        return <NoItemsYet text={'No Mastery Yet'} image={Images.emptyFolder} />;
    }

    return (
        <div>
            <BaselineCriteriaOption />
            {goalById?.targetMastery?._id && <TargetMasteryCard goalById={goalById} />}
            {goalById?.goalMastery?._id && <GoalMasteryCard goalById={goalById} />}
            <TargetMaintenance />
        </div>
    );
};
